import React, { useCallback, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { useTheme } from 'styled-components';
import { Assets } from "../../assets";
import { Close } from "../../assets/Close";
import { ClearButton, HelpMessage, IconBox, Input, InputContainer, LabelContainer, PlaceholderIconBox, RightContainer, TextArea, TextAreaContainer, TextFieldContainer } from "./TextField.styles";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A TextField component. it can be either a input or a textarea, depending on the multiline prop.
 *
 * @param align Text and placeholder alignment of the input
 * @param clearable If the input is clearable
 * @param disabled If the input is disabled
 * @param error If the input has an error
 * @param helpGutter If the help message should have a gutter
 * @param helpMessage Help message to be displayed
 * @param label Label of the input
 * @param leftIcon Icon to be displayed on the left side of the input
 * @param mask Mask to be applied to the input
 * @param multiline If the input should be multiline
 * @param name Name of the input
 * @param onChange Function to be called when the input value changes
 * @param onFocus Function to be called when the input is focused
 * @param onBlur Function to be called when the input is blurred
 * @param placeholder Placeholder of the input
 * @param rightIcon Icon to be displayed on the right side of the input
 * @param rows Number of rows for the textarea
 * @param size Size of the input
 * @param value Value of the input
 */
const TextField = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    align = 'left',
    clearable,
    disabled,
    error,
    helpGutter = true,
    helpMessage,
    label,
    leftIcon,
    mask,
    multiline,
    name,
    placeholder,
    rightIcon = '',
    rows,
    size = 'md',
    value,
    onBlur,
    onChange,
    onFocus,
    ...rest
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const elemId = useRef(nanoid(10));
  const LeftIcon = leftIcon && Assets[leftIcon];
  const RightIcon = rightIcon && Assets[rightIcon];
  const handleClear = useCallback(() => {
    if (ref) {
      const input = document.getElementById(elemId.current);
      if (input) input.value = '';
    } else {
      onChange?.({
        target: {
          value: ''
        }
      });
    }
  }, []);
  return /*#__PURE__*/_jsxs(TextFieldContainer, {
    size: size,
    disabled: disabled,
    children: [/*#__PURE__*/_jsx(LabelContainer, {
      htmlFor: elemId.current,
      children: label
    }), multiline ? /*#__PURE__*/_jsxs(TextAreaContainer, {
      error: error,
      isFocused: isFocused,
      children: [LeftIcon && /*#__PURE__*/_jsx(IconBox, {
        children: /*#__PURE__*/_jsx(LeftIcon, {
          size: size === 'lg' ? 'md' : size,
          color: theme?.colors?.neutralL5
        })
      }), align === 'center' && RightIcon && !LeftIcon && /*#__PURE__*/_jsx(PlaceholderIconBox, {
        size: size
      }), /*#__PURE__*/_jsx(TextArea, {
        ...rest,
        id: elemId.current,
        align: align,
        disabled: disabled,
        name: name,
        onChange: e => onChange?.(e),
        ref: ref,
        rows: rows,
        value: value,
        placeholder: placeholder,
        onFocus: e => {
          setIsFocused(true);
          onFocus?.(e);
        },
        onBlur: e => {
          setIsFocused(false);
          onBlur?.(e);
        },
        autoComplete: "off"
      }), /*#__PURE__*/_jsxs(RightContainer, {
        children: [clearable && /*#__PURE__*/_jsx(IconBox, {
          children: /*#__PURE__*/_jsx(ClearButton, {
            size: size,
            onClick: handleClear,
            children: /*#__PURE__*/_jsx(Close, {
              size: size === 'lg' ? 'md' : size,
              color: theme.colors.secondaryL3
            })
          })
        }), align === 'center' && LeftIcon && !RightIcon && /*#__PURE__*/_jsx(PlaceholderIconBox, {
          size: size
        }), RightIcon && /*#__PURE__*/_jsx(IconBox, {
          children: /*#__PURE__*/_jsx(RightIcon, {
            size: size === 'lg' ? 'md' : size,
            color: theme?.colors?.neutralL5
          })
        })]
      })]
    }) : /*#__PURE__*/_jsxs(InputContainer, {
      error: error,
      isFocused: isFocused,
      size: size,
      children: [LeftIcon && /*#__PURE__*/_jsx(IconBox, {
        children: /*#__PURE__*/_jsx(LeftIcon, {
          size: size === 'lg' ? 'md' : size,
          color: theme?.colors?.neutralL5
        })
      }), align === 'center' && RightIcon && !LeftIcon && /*#__PURE__*/_jsx(PlaceholderIconBox, {
        size: size
      }), /*#__PURE__*/_jsx(Input, {
        ...rest,
        id: elemId.current,
        align: align,
        disabled: disabled,
        name: name,
        onChange: e => onChange?.(e),
        onFocus: e => {
          setIsFocused(true);
          onFocus?.(e);
        },
        onBlur: e => {
          setIsFocused(false);
          onBlur?.(e);
        },
        placeholder: placeholder,
        inputRef: ref,
        value: value,
        mask: mask ?? '',
        alwaysShowMask: false,
        autoComplete: "off"
      }), clearable && /*#__PURE__*/_jsx(IconBox, {
        children: /*#__PURE__*/_jsx(ClearButton, {
          size: size,
          onClick: handleClear,
          children: /*#__PURE__*/_jsx(Close, {
            size: size === 'lg' ? 'md' : size,
            color: theme.colors.secondaryL3
          })
        })
      }), RightIcon && /*#__PURE__*/_jsx(IconBox, {
        children: /*#__PURE__*/_jsx(RightIcon, {
          size: size === 'lg' ? 'md' : size,
          color: theme?.colors?.neutralL5
        })
      }), align === 'center' && LeftIcon && !RightIcon && /*#__PURE__*/_jsx(PlaceholderIconBox, {
        size: size
      })]
    }), helpGutter && /*#__PURE__*/_jsx(HelpMessage, {
      error: error,
      children: helpMessage
    })]
  });
});
TextField.displayName = 'TextField';
export { TextField };